/**
 * Converts a value from seconds (number or string) to minutes.
 * @param sec A value in seconds.
 * @returns Basically, `Number(sec) / 60`, rounded to one fractional digit.
 * If `sec` cannot be converted to a number, 0 is returned.
 */
export const transformSecondsToMinutes = (sec: string | number): number => {
  const secAsNumber = Number(sec)
  if (!secAsNumber) {
    return 0
  }
  const minutes = Math.round((secAsNumber * 10) / 60) / 10 // rounding with max. 1 fractional digit
  return minutes
}

/**
 * Takes a form input value (string), converts it to a number, and that number from minutes to seconds.
 *
 * Tolerates localized number formats.
 * @param min A value in minutes.
 * @returns Basically, `Math.round(Number(min) * 60)`
 */
export const transformInputToSeconds = (min: string): number => {
  const minAsNumber = transformInputToNumber(min)
  const seconds = Math.round(minAsNumber * 60)
  return seconds
}

/**
 * Converts a number to a localized string.
 *
 * @returns
 * If the input is null-ish, "0" is returned.
 * If the input is not of type number, the output of input's toString() method is returned.
 */
export const transformNumberToString = (num: string | number): string => {
  if (undefined === num || null === num) {
    return "0"
  }
  if (typeof num === "number") {
    return num.toLocaleString(undefined, {
      useGrouping: false, // disable thousands separator
    })
  }
  return num.toString()
}

/**
 * Takes a string which is assumed to represent a number in a localized format.
 * Converts that into the same number's string representation in international format.
 *
 * @example
 * "1,5" (de) -> "1.5"
 * "1.5" -> "1.5"
 * "1234,5" (de) -> "1234.5"
 * "1234.5" -> "1234.5"
 * "1.234,5" (de) -> "1234.5"
 * "1,234.5" -> "1234.5"
 */
export const transformInputToInternational = (input: string): string => {
  const internationalDecimal = "."
  const lastPeriod = input.lastIndexOf(".")
  const lastComma = input.lastIndexOf(",")

  if (lastComma === -1) {
    // There's no comma. Assume it's an integer, or a decimal number in international format already.
    return input
  }

  // There is a comma. What is its role?

  if (lastPeriod === -1) {
    // There's no period. Assume the comma is the decimal separator.
    return input.replaceAll(",", internationalDecimal)
  }

  // There's both, period and comma. Assume the last one is the decimal separator.
  const decimalSep = lastPeriod > lastComma ? "." : ","
  const thousandsSep = "." === decimalSep ? "," : "."
  return input.replaceAll(thousandsSep, "").replaceAll(decimalSep, ".")
}

/**
 * Takes a form input value (string), and converts it to a number.
 *
 * Tolerates localized number formats.
 * @returns If `input` cannot be converted to a number, 0 is returned.
 */
export const transformInputToNumber = (input: string): number => {
  const inputAsInternational = transformInputToInternational(input)
  const inputAsNumber = Number(inputAsInternational)
  if (!inputAsNumber) {
    return 0
  }
  return inputAsNumber
}
